import { NgIf, NgFor } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { User } from 'src/app/models/User';
import { Page } from 'src/app/models/page.model';
import { UserFeedback } from 'src/app/models/UserFeedback.model';

import { UserFeedbackService } from 'src/app/services/userFeedback.service';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    COUNT,
    OFFSET,
    STATUS,
    SUBMITTEDBYID,
    USERVIEW,
} from 'src/app/constants/queryParamConstants';
import { ITEM_COUNT, OFFSET_START } from 'src/app/constants/searchConstants';
import { MatDialog } from '@angular/material/dialog';
import { SeeDetailsComponent } from './see-details/see-details.component';
import { UserTicketStatus } from 'src/app/enums/userTicketStatus.enum';
import { UserFeedbackStatuses } from 'src/app/interfaces/userFeedback-Form';
import { FeedbackCardComponent } from './feedback-card/feedback-card.component';

@Component({
    selector: 'app-user-feedback',
    imports: [
        ReactiveFormsModule,
        MatSlideToggleModule,
        NgIf,
        MatFormFieldModule,
        MatSelectModule,
        NgFor,
        MatOptionModule,
        MatButtonModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatInputModule,
        MatListModule,
        MatPaginatorModule,
        MatChipsModule,
        FeedbackCardComponent,
        MatTooltipModule,
        MatCardModule,
    ],
    templateUrl: './user-feedback.component.html',
    styleUrl: './user-feedback.component.scss',
})
export class UserFeedbackComponent implements OnInit {
    constructor(
        private userFeedbackService: UserFeedbackService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
    ) {}
    @Input() userView?: boolean;

    searchForm = new FormGroup({
        userSearch: new FormControl<string>(null),
        statusSearch: new FormControl<string>(null),
    });
    users: User[];

    protected userFeedbackPaginatedResult = new Page<UserFeedback>({
        countRequested: 10,
        offsetRequested: 0,
        items: [],
        totalCount: 0,
    });

    viewer: string = 'User Feedback';
    pageIndex: number = 0;
    loadingUserFeedback: boolean = false;
    selectedUserFeedback: UserFeedback = null;

    queryParams = new Map<string, boolean | string | number>();
    feedbackStatuses: UserFeedbackStatuses[] = [
        UserTicketStatus.New,
        UserTicketStatus.Processed,
        UserTicketStatus.Duplicate,
    ];

    ngOnInit(): void {
        if (this.userView) {
            this.viewer = 'My Feedback';
        } else {
            this.userFeedbackService
                .get(['users-feedback'])
                .subscribe((response: Page<User>) => {
                    this.users = response.items;
                });
        }

        this.populateFilters();
        this.setQueryParams();
    }

    setQueryParams(): void {
        this.queryParams.set(COUNT, ITEM_COUNT);

        if (
            this.searchForm.controls.userSearch.value &&
            this.searchForm.controls.userSearch.value !== ''
        ) {
            this.queryParams.set(
                SUBMITTEDBYID,
                this.searchForm.controls.userSearch.value,
            );
        } else {
            this.queryParams.delete(SUBMITTEDBYID);
        }

        if (this.pageIndex) {
            this.queryParams.set(OFFSET, this.pageIndex * ITEM_COUNT);
        } else {
            this.queryParams.set(OFFSET, OFFSET_START);
        }

        if (
            this.searchForm.controls.statusSearch.value &&
            this.searchForm.controls.statusSearch.value !== ''
        ) {
            this.queryParams.set(
                STATUS,
                this.searchForm.controls.statusSearch.value,
            );
        } else {
            this.queryParams.delete(STATUS);
        }

        if (this.userView) {
            this.queryParams.set(USERVIEW, true);
        } else {
            this.queryParams.delete(USERVIEW);
        }

        this.updateUrl();
        this.getFeedback();
    }

    getFeedback() {
        if (this.userView) {
            this.loadingUserFeedback = true;
            this.userFeedbackService
                .get(['my-feedbacks'], this.queryParams)
                .subscribe((response: Page<UserFeedback>) => {
                    this.userFeedbackPaginatedResult = response;
                    this.loadingUserFeedback = false;
                });
        } else {
            this.loadingUserFeedback = true;
            this.userFeedbackService
                .get(['feedbacks'], this.queryParams)
                .subscribe((response: Page<UserFeedback>) => {
                    this.userFeedbackPaginatedResult = response;
                    this.loadingUserFeedback = false;
                });
        }
    }

    protected changePage(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.setQueryParams();
    }

    private populateFilters(): void {
        const params: Params = this.activatedRoute.snapshot.queryParams;

        if (STATUS in params) {
            const status = (params[STATUS] as string).toUpperCase();

            const matchingStatus = this.feedbackStatuses.find(
                (s) => s.toUpperCase() === status,
            );

            if (matchingStatus) {
                this.searchForm.controls.statusSearch.setValue(matchingStatus);
            }
        }

        if (SUBMITTEDBYID in params) {
            this.searchForm.controls.userSearch.setValue(
                params[SUBMITTEDBYID] as string,
            );
        }
    }

    private updateUrl(): void {
        const queryParams: Params = {};

        const status = this.searchForm.controls.statusSearch.value;
        if (status) {
            queryParams[STATUS] = status;
        }

        const submittedById = this.searchForm.controls.userSearch.value;
        if (submittedById) {
            queryParams[SUBMITTEDBYID] = submittedById;
        }

        void this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
        });
    }

    protected displayTicketDetailsMenu(selectedTicket: UserFeedback): void {
        if (
            selectedTicket.status != UserTicketStatus.Processed &&
            selectedTicket.status != UserTicketStatus.Duplicate
        ) {
            selectedTicket.status = UserTicketStatus.Read;
        }

        this.dialog.open(SeeDetailsComponent, {
            width: '80%',
            height: 'fit',
            data: {
                userView: this?.userView,
                selectedTicket: selectedTicket,
            },
        });
    }
}
